import axios, { type AxiosAdapter, type AxiosInstance } from "axios";

import { isRead, withRetry } from "./retry";

/**
 * Every API call runs the auth gate Lambda first, and each warm Lambda holds its own
 * database connections. A burst of parallel calls scales that Lambda out and drains the
 * database, so one tab keeps only a few calls in flight. Writes go to the front of the
 * queue: they are what the user is waiting on.
 */
export const MAX_IN_FLIGHT = 6;

export function createLimiter(max: number) {
    let active = 0;
    const waiting: Array<() => void> = [];

    return async function run<T>(task: () => Promise<T>, { urgent = false } = {}): Promise<T> {
        if (active >= max) {
            // The finishing task hands its slot straight over, so a newcomer can't take it first
            await new Promise<void>(resolve => (urgent ? waiting.unshift(resolve) : waiting.push(resolve)));
        } else {
            active++;
        }
        try {
            return await task();
        } finally {
            const next = waiting.shift();
            if (next) next();
            else active--;
        }
    };
}

export function limitInFlight(instance: AxiosInstance, max = MAX_IN_FLIGHT) {
    // Test doubles of axios have no adapters to wrap
    if (typeof axios.getAdapter !== "function" || !instance?.defaults) return;
    const send = axios.getAdapter(instance.defaults.adapter) as AxiosAdapter;
    const run = createLimiter(max);
    instance.defaults.adapter = config => withRetry(config, () => run(() => send(config), { urgent: !isRead(config) }));
}
